'use client';

/**
 * THE last-resort error boundary for an app ROOT.
 *
 * WHY IT EXISTS. Until 2026-08-20 no standalone SPA root carried one -- 0 of 7 -- and neither did the embedded MFE
 * mount factory. An uncaught render error therefore unwound the React root and left a WHITE PAGE: no message, no
 * recovery, and a report only in the console, because a boundary-less throw never reaches `window.onerror` either. That
 * is also why this reports through `trackComponentError` rather than leaving it to `TelemetryProvider`: a CAUGHT error
 * is invisible to the provider's window listener (`components/lib/api/error-telemetry.ts` documents exactly this gap).
 *
 * IT IS NOT A FEATURE BOUNDARY. `PropertyFeedErrorBoundary` and `TradeErrorBoundary` stay: each knows what its own
 * subtree was doing and can say so. This one knows nothing about the surface it wraps and deliberately says nothing
 * about it -- a root boundary that guesses is worse than one that names the recoverable action and stops.
 *
 * RETRY REMOUNTS RATHER THAN RESETTING -- see {@link nextRootErrorRetryState}.
 */

import * as React from 'react';
import { trackComponentError } from '@federa/components/lib/api/error-telemetry';
import { ROOT_ERROR_BOUNDARY_COPY } from '@federa/const/root-error-boundary';
import { Alert } from '../feedback/Alert.tsx';
import { Button } from '../primitives/Button.tsx';
import { spacing } from '../css-refs.spacing.ts';
import { FLEX_COLUMN_GAP3_STYLE, FLEX_COLUMN_START_GAP3_STYLE as FALLBACK_BODY_STYLE } from '../common/style-primitives.ts';
import { ERROR_ACTION_LABELS } from './errorLabels.ts';
import type { CSSProperties, ReactNode } from 'react';

const FALLBACK_FRAME_STYLE: CSSProperties = {
	...FLEX_COLUMN_GAP3_STYLE,
	padding: spacing[6],
};

/** The rendered fallback, split out so the class below stays a lifecycle shell with no layout in it. */
function RootErrorFallback(props: { title: string; body: string; onRetry: () => void }) {
	return (
		<div style={FALLBACK_FRAME_STYLE}>
			<Alert variant="error" title={props.title}>
				<span style={FALLBACK_BODY_STYLE}>
					<span>{props.body}</span>
					<Button variant="outline" size="sm" onClick={props.onRetry}>
						{ERROR_ACTION_LABELS.tryAgain}
					</Button>
				</span>
			</Alert>
		</div>
	);
}

/** FILE-LOCAL, not exported: every reader is in this leaf (the retry transition, the class, and its
 *  `getDerivedStateFromError`), so consumer cardinality outside it is ZERO and there is nothing for a
 *  `types/<topic>.ts` leaf to own -- QUAL-TYPE-SSOT-001's `fix` demotes rather than migrates that class. */
interface RootErrorBoundaryState {
	error: Error | null;
	/** Bumped on every retry so the recovered subtree is a FRESH one (see {@link nextRootErrorRetryState}). */
	resetKey: number;
}

/**
 * The retry transition, exported because it is the arm that decides whether the button WORKS and it fails silently when
 * wrong.
 *
 * Clearing the error alone re-renders the SAME element tree, so React re-uses the subtree that just threw and the throw
 * repeats on the next commit -- a button that looks dead. Bumping a key forces React to build the subtree from scratch,
 * which is what recovers a transient failure (a lazily-imported chunk that 404'd, a race on first data). The bump is
 * therefore STRICTLY monotonic rather than a toggle: a second failure must get a third subtree, not the first one back.
 *
 * Pure, because StrictMode double-invokes a `setState` updater.
 */
export function nextRootErrorRetryState(prev: RootErrorBoundaryState): RootErrorBoundaryState {
	return { error: null, resetKey: prev.resetKey + 1 };
}

interface RootErrorBoundaryProps {
	/** Names the boundary in the telemetry record, so a report says WHICH root died rather than only that one did. */
	boundaryName: string;
	/** Surface-specific overrides; both default to the shared root copy. */
	title?: string;
	body?: string;
	children: ReactNode;
}

export class RootErrorBoundary extends React.Component<RootErrorBoundaryProps, RootErrorBoundaryState> {
	constructor(props: RootErrorBoundaryProps) {
		super(props);
		this.state = { error: null, resetKey: 0 };
	}

	static getDerivedStateFromError(error: Error): Pick<RootErrorBoundaryState, 'error'> {
		return { error };
	}

	override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		trackComponentError(this.props.boundaryName, error, {
			componentStack: errorInfo.componentStack ?? undefined,
		});
	}

	private readonly handleRetry = () => {
		this.setState(nextRootErrorRetryState);
	};

	override render() {
		if (this.state.error) {
			return (
				<RootErrorFallback
					title={this.props.title ?? ROOT_ERROR_BOUNDARY_COPY.title}
					body={this.props.body ?? ROOT_ERROR_BOUNDARY_COPY.body}
					onRetry={this.handleRetry}
				/>
			);
		}
		return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
	}
}
